import { useCallback, useEffect, useRef, useState } from 'react'
import {
  createWebHapticBridge,
  startEccentricHapticStream,
  stopEccentricHapticStream,
  type CalibrationHapticBridge,
} from './calibrationHaptics'
import { speakCalibrationCue } from './audioSessionDucking'
import {
  SET_COUNTDOWN_SECONDS,
  TEMPO_PHASE_MS,
  type CalibrationSetResult,
  type RepAdvanceMode,
  type RepConfirmationSource,
  type RepVelocitySample,
  type TempoPhase,
} from './tempoConstants'
import {
  bannerMessageForFlag,
  buildRepVelocitySample,
  coachMessageForFlag,
  tempoComplianceRate,
} from './velocityDeviation'

export type RingVariant = 'default' | 'amber' | 'stall'

export type UseCalibrationSetPacerOptions = {
  targetReps: number
  hapticBridge?: CalibrationHapticBridge
  autoStart?: boolean
  /** Manual mode waits for explicit rep confirmation after each tempo cycle. */
  repAdvanceMode?: RepAdvanceMode
  onSetComplete?: (result: CalibrationSetResult) => void
}

export type UseCalibrationSetPacerResult = {
  phase: TempoPhase
  /** 3 → 2 → 1 → 0 (GO flash) during pre-set countdown. */
  countdownTick: number | null
  repCount: number
  ringVariant: RingVariant
  isometricFlash: boolean
  banner: string | null
  coachMessage: string | null
  velocityHistory: RepVelocitySample[]
  muscularFailure: boolean
  setActive: boolean
  repAdvanceMode: RepAdvanceMode
  /** User tapped Begin set — runs 3-2-1-GO then first rep. */
  beginCountdown: () => void
  startSet: () => void
  confirmRep: (source: RepConfirmationSource) => void
  endSet: () => CalibrationSetResult
}

export function useCalibrationSetPacer({
  targetReps,
  hapticBridge: hapticBridgeProp,
  autoStart: _autoStart = true,
  repAdvanceMode = 'auto',
  onSetComplete,
}: UseCalibrationSetPacerOptions): UseCalibrationSetPacerResult {
  const defaultBridgeRef = useRef<CalibrationHapticBridge | null>(null)
  if (!defaultBridgeRef.current) {
    defaultBridgeRef.current = createWebHapticBridge()
  }
  const hapticBridgeRef = useRef(hapticBridgeProp ?? defaultBridgeRef.current)
  hapticBridgeRef.current = hapticBridgeProp ?? defaultBridgeRef.current

  const [phase, setPhase] = useState<TempoPhase>('prep')
  const [countdownTick, setCountdownTick] = useState<number | null>(null)
  const [repCount, setRepCount] = useState(0)
  const [ringVariant, setRingVariant] = useState<RingVariant>('default')
  const [isometricFlash, setIsometricFlash] = useState(false)
  const [banner, setBanner] = useState<string | null>(null)
  const [coachMessage, setCoachMessage] = useState<string | null>(null)
  const [velocityHistory, setVelocityHistory] = useState<RepVelocitySample[]>([])
  const [muscularFailure, setMuscularFailure] = useState(false)
  const [setActive, setSetActive] = useState(false)

  const phaseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const phaseRef = useRef<TempoPhase>('prep')
  const lastRepTimestampRef = useRef<number | null>(null)
  const repCountRef = useRef(0)
  const muscularFailureRef = useRef(false)
  const velocityHistoryRef = useRef<RepVelocitySample[]>([])
  const onSetCompleteRef = useRef(onSetComplete)
  const setActiveRef = useRef(false)
  const repAdvanceModeRef = useRef(repAdvanceMode)
  const targetRepsRef = useRef(targetReps)

  useEffect(() => {
    repAdvanceModeRef.current = repAdvanceMode
  }, [repAdvanceMode])

  useEffect(() => {
    targetRepsRef.current = targetReps
  }, [targetReps])

  useEffect(() => {
    phaseRef.current = phase
  }, [phase])

  const clearCountdownTimer = useCallback(() => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current)
      countdownTimerRef.current = null
    }
  }, [])

  const clearPhaseTimer = useCallback(() => {
    if (phaseTimerRef.current) {
      clearTimeout(phaseTimerRef.current)
      phaseTimerRef.current = null
    }
  }, [])

  useEffect(() => {
    onSetCompleteRef.current = onSetComplete
  }, [onSetComplete])

  const finishSet = useCallback(() => {
    clearPhaseTimer()
    stopEccentricHapticStream(hapticBridgeRef.current)
    setActiveRef.current = false
    setSetActive(false)
    setPhase('idle')

    const result: CalibrationSetResult = {
      repsCompleted: repCountRef.current,
      targetReps: targetRepsRef.current,
      velocityDeviationHistory: velocityHistoryRef.current,
      tempoComplianceRate: tempoComplianceRate(velocityHistoryRef.current),
      muscularFailure: muscularFailureRef.current,
      rir: muscularFailureRef.current ? 0 : Math.max(0, targetRepsRef.current - repCountRef.current),
    }
    onSetCompleteRef.current?.(result)
    return result
  }, [clearPhaseTimer])

  const recordRepConfirmation = useCallback(
    (source: RepConfirmationSource) => {
      const now = Date.now()
      const prev = lastRepTimestampRef.current
      lastRepTimestampRef.current = now

      if (prev == null) return

      const sample = buildRepVelocitySample(repCountRef.current, now - prev, source)
      velocityHistoryRef.current = [...velocityHistoryRef.current, sample]
      setVelocityHistory(velocityHistoryRef.current)

      if (sample.complianceFlag === 'rusher') {
        setRingVariant('amber')
        setBanner(bannerMessageForFlag('rusher'))
      } else if (sample.complianceFlag !== 'stall') {
        setRingVariant('default')
        setBanner(null)
      }

      if (sample.complianceFlag === 'stall') {
        setRingVariant('stall')
        const msg = coachMessageForFlag('stall')
        setCoachMessage(msg)
        if (msg) void speakCalibrationCue(msg)
        muscularFailureRef.current = true
        setMuscularFailure(true)
        finishSet()
      }
    },
    [finishSet],
  )

  const runRepCycleRef = useRef<() => void>(() => undefined)

  useEffect(() => {
    runRepCycleRef.current = () => {
      if (!setActiveRef.current || muscularFailureRef.current) return

      setPhase('eccentric')
      startEccentricHapticStream(hapticBridgeRef.current)

      phaseTimerRef.current = setTimeout(() => {
        stopEccentricHapticStream(hapticBridgeRef.current)
        setPhase('isometric')
        hapticBridgeRef.current.isometricDoublePulse()
        setIsometricFlash(true)

        phaseTimerRef.current = setTimeout(() => {
          setIsometricFlash(false)
          setPhase('concentric')

          phaseTimerRef.current = setTimeout(() => {
            hapticBridgeRef.current.repCompletionThud()

            if (repAdvanceModeRef.current === 'manual') {
              lastRepTimestampRef.current = Date.now()
              setPhase('await_confirm')
              return
            }

            setPhase('reset')
            recordRepConfirmation('screen_tap')
            repCountRef.current += 1
            setRepCount(repCountRef.current)

            if (muscularFailureRef.current) return

            if (repCountRef.current >= targetRepsRef.current) {
              finishSet()
              return
            }

            runRepCycleRef.current()
          }, TEMPO_PHASE_MS.concentric)
        }, TEMPO_PHASE_MS.isometric)
      }, TEMPO_PHASE_MS.eccentric)
    }
  }, [finishSet, recordRepConfirmation])

  const runRepCycle = useCallback(() => {
    runRepCycleRef.current()
  }, [])

  const startSet = useCallback(() => {
    clearPhaseTimer()
    clearCountdownTimer()
    stopEccentricHapticStream(hapticBridgeRef.current)
    muscularFailureRef.current = false
    repCountRef.current = 0
    velocityHistoryRef.current = []
    lastRepTimestampRef.current = Date.now()
    setRepCount(0)
    setCountdownTick(null)
    setVelocityHistory([])
    setMuscularFailure(false)
    setBanner(null)
    setCoachMessage(null)
    setRingVariant('default')
    setActiveRef.current = true
    setSetActive(true)
    runRepCycle()
  }, [clearCountdownTimer, clearPhaseTimer, runRepCycle])

  const beginCountdown = useCallback(() => {
    clearPhaseTimer()
    clearCountdownTimer()
    setPhase('countdown')
    setCountdownTick(SET_COUNTDOWN_SECONDS)
    hapticBridgeRef.current.isometricDoublePulse()

    let tick = SET_COUNTDOWN_SECONDS
    countdownTimerRef.current = setInterval(() => {
      tick -= 1
      if (tick > 0) {
        setCountdownTick(tick)
        hapticBridgeRef.current.isometricDoublePulse()
        return
      }

      clearCountdownTimer()
      setCountdownTick(0)
      hapticBridgeRef.current.repCompletionThud()

      phaseTimerRef.current = setTimeout(() => {
        startSet()
      }, 450)
    }, 1000)
  }, [clearCountdownTimer, clearPhaseTimer, startSet])

  const confirmRep = useCallback(
    (source: RepConfirmationSource) => {
      if (!setActiveRef.current || muscularFailureRef.current) return
      if (repAdvanceModeRef.current === 'manual' && phaseRef.current !== 'await_confirm') return
      clearPhaseTimer()
      stopEccentricHapticStream(hapticBridgeRef.current)
      hapticBridgeRef.current.repCompletionThud()
      recordRepConfirmation(source)
      repCountRef.current += 1
      setRepCount(repCountRef.current)

      if (muscularFailureRef.current) return

      if (repCountRef.current >= targetRepsRef.current) {
        finishSet()
        return
      }

      runRepCycle()
    },
    [clearPhaseTimer, finishSet, recordRepConfirmation, runRepCycle],
  )

  const endSet = useCallback(() => finishSet(), [finishSet])

  useEffect(() => {
    return () => {
      clearPhaseTimer()
      clearCountdownTimer()
      stopEccentricHapticStream(hapticBridgeRef.current)
      setActiveRef.current = false
    }
  }, [clearCountdownTimer, clearPhaseTimer])

  return {
    phase,
    countdownTick,
    repCount,
    ringVariant,
    isometricFlash,
    banner,
    coachMessage,
    velocityHistory,
    muscularFailure,
    setActive,
    repAdvanceMode,
    beginCountdown,
    startSet,
    confirmRep,
    endSet,
  }
}
